import { getAvatarProps } from '@AppModels/AvatarUtils';
import { Avatar } from '@HoudiniUI/Avatar';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { Button } from '../../../components-v2/Button';
import { SPACE } from '../../../components-v2/Theme/theme';
import { Dropdown, DropdownContent, DropdownTrigger } from '../../../components-v2/Dropdown';
import { ChevronLeft } from '../../svgs/icons/ChevronLeft';
import { ChevronRight } from '../../svgs/icons/ChevronRight';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../Tooltip';
import { styleSheet as navStyleSheet } from '../NavBar/styles';
import { styleSheet } from './styles';

/** Opening or closing the nav lasts until the next navigation, when the route and screen width decide again. */
export function useNavCompactToggle(autoCompact: boolean) {
	// Every navigation creates a new location, so returning to a toggled path doesn't bring its override back.
	// Hash history has no `location.key` to compare instead.
	const location = useLocation();
	const [override, setOverride] = React.useState<{ compact: boolean; location: typeof location } | null>(null);
	const compactLayout = override?.location === location ? override.compact : autoCompact;
	const toggleCompactLayout = () => setOverride({ compact: !compactLayout, location });
	return [compactLayout, toggleCompactLayout] as const;
}

const NAV_TOOLTIP_DELAY_MS = 200;

export const NavTooltip = ({
	children,
	disabled,
	label,
	side = 'right',
}: {
	children: React.ReactNode;
	disabled?: boolean;
	label: string;
	side?: 'right' | 'top';
}) => (
	<Tooltip delayDuration={NAV_TOOLTIP_DELAY_MS} open={disabled ? false : undefined}>
		<TooltipTrigger asChild>
			<div className={css(styleSheet.tooltipTrigger)}>{children}</div>
		</TooltipTrigger>
		<TooltipContent side={side} sideOffset={SPACE[12]}>
			{label}
		</TooltipContent>
	</Tooltip>
);

export const NavFooter = ({ children }: { children: React.ReactNode }) => (
	<div className={css(styleSheet.footer)}>{children}</div>
);

const _NavUserMenu = ({
	children,
	compactLayout,
	onOpenChange,
	open,
}: {
	children: React.ReactNode;
	compactLayout: boolean;
	onOpenChange?: (open: boolean) => void;
	open?: boolean;
}) => {
	const userSession = useUserSession();
	const userName = getDisplayName(userSession.user);
	const companyName = userSession.account?.companyName;
	return (
		<Dropdown open={open} onOpenChange={onOpenChange ? nextOpen => onOpenChange(nextOpen) : undefined}>
			{/* Above, not beside: the collapse toggle sits right of the avatar. */}
			<NavTooltip disabled={!compactLayout} label={userName} side='top'>
				<DropdownTrigger
					aria-label={compactLayout ? `${userName}, account menu` : undefined}
					className={css(navStyleSheet.link, styleSheet.userTrigger, compactLayout && styleSheet.userTriggerCompact)}
				>
					<Avatar {...getAvatarProps(userSession.user)} styles={[styleSheet.avatar]} smallText={true} />
					{!compactLayout && (
						<span className={css(styleSheet.userInfo)}>
							<span className={css(styleSheet.userName)}>{userName}</span>
							{companyName ? <span className={css(styleSheet.companyName)}>{companyName}</span> : null}
						</span>
					)}
				</DropdownTrigger>
			</NavTooltip>
			<DropdownContent side='top' align='start' styles={[styleSheet.menu]}>
				{children}
			</DropdownContent>
		</Dropdown>
	);
};

export const NavUserMenu = observer(_NavUserMenu);

export const NavCompactToggle = ({ compactLayout, onToggle }: { compactLayout: boolean; onToggle: () => void }) => (
	<NavTooltip label={compactLayout ? 'Open' : 'Close'}>
		<Button
			type='button'
			variant='outline'
			size='icon'
			aria-expanded={!compactLayout}
			aria-label={compactLayout ? 'Open navigation' : 'Close navigation'}
			onClick={onToggle}
			styles={[styleSheet.toggle, compactLayout && styleSheet.toggleCompact]}
		>
			{compactLayout ? (
				<ChevronRight className={css(styleSheet.toggleIcon)} />
			) : (
				<ChevronLeft className={css(styleSheet.toggleIcon)} />
			)}
		</Button>
	</NavTooltip>
);
